/**
 * Stale-chunk recovery — reload into the newest build when a dynamic import
 * fails to fetch.
 *
 * The prompt-mode service worker (see `vite.config.ts` / `UpdatePrompt.tsx`)
 * keeps an old bundle running until the user accepts the "Update available"
 * toast, on the invariant that a running bundle only ever references chunks in
 * its own precache — so a new deploy can't pull code out from under it. That
 * holds for every precached chunk, but the settings panes are deliberately
 * excluded from the precache (`globIgnores: ["**​/settings-pane-*.js"]`) and
 * fetched live over the network instead: they only work online, so installing
 * them would be pure waste. The cost of that choice is that they are the one
 * part of the app a stale tab cannot open on its own — after a deploy,
 * Cloudflare's asset store only has the newest build's hashed files, so the
 * request for the old hash 404s (the SPA fallback serves `index.html`, and the
 * browser refuses to run HTML as a module) and the pane dead-ends on "Failed to
 * fetch dynamically imported module".
 *
 * So the exclusion is only safe if this module is: taking the update is the
 * ONLY way a stale tab can reach a settings pane, and this is what takes it.
 *
 * Vite fires `vite:preloadError` on `window` when its `__vitePreload` helper
 * can't load a lazy chunk. For a content-hashed immutable asset, that failure
 * means the deploy moved on — so the fix is to reload into the fresh bundle
 * instead of dead-ending on a manual hard refresh.
 *
 * Reloading is not by itself enough, and that is the part that was broken here
 * before: a reload of a *controlled* page is answered by the service worker
 * that's already active, which serves `index.html` out of the OLD precache —
 * the same stale bundle, referencing the same missing chunk. A waiting worker
 * does not activate on reload either (that needs every client to go away, which
 * a reload never produces). The reload therefore has to be preceded by
 * activating the worker that's ready to take over — which first means finding
 * it, and `registration.waiting` is null for most of the window where one
 * exists (see `workerReadyToTakeOver`) — and has to wait until that worker
 * actually controls the page. An uncontrolled page — the visit where the worker
 * first
 * registers, since `clientsClaim` is off — is the opposite case: nothing
 * intercepts, so a plain reload reaches the network and is already correct.
 *
 * Loop guard: we key on the *failed chunk URL* (carried in the error message),
 * not a bare "already reloaded" flag. A chunk hash is immutable, so if the same
 * URL fails again after our reload the chunk is genuinely gone — we stop and let
 * the error surface rather than hot-loop. A subsequent deploy changes the hash,
 * yielding a new URL, so recovery is armed again.
 *
 * If recovery can't land (no update to take, or the new worker never claims the
 * page), the failed import still rejects into the router's error boundary — the
 * backstop is a visible error pane, not a hang.
 */

const RELOAD_KEY = "hearth:stale-chunk-reload-url";

// Vite dispatches a plain `Event` with the error tacked on as `.payload`; the
// property isn't in the DOM lib types.
type PreloadErrorEvent = Event & { payload?: unknown };

/**
 * The bits of the platform this module touches. Narrow structural types (rather
 * than the DOM ones) so a test can hand it a plain object — the real
 * `ServiceWorkerContainer` satisfies them.
 */
export interface RecoveryDeps {
  /** Absent when the browser has no service-worker support. */
  serviceWorker?: {
    getRegistration(): Promise<Registration | undefined>;
    addEventListener(
      type: "controllerchange",
      listener: () => void,
      options: { once: true },
    ): void;
  };
  storage: Pick<Storage, "getItem" | "setItem">;
  reload: () => void;
}

interface Registration {
  update(): Promise<unknown>;
  readonly installing: PendingWorker | null;
  readonly waiting: PendingWorker | null;
}

interface PendingWorker {
  /** A `ServiceWorkerState`; "installed" is the state we're waiting for. */
  readonly state: string;
  postMessage(message: unknown): void;
  addEventListener(type: "statechange", listener: () => void): void;
  removeEventListener(type: "statechange", listener: () => void): void;
}

/** Resolve once `worker` leaves the "installing" state, however it leaves it. */
function installSettled(worker: PendingWorker): Promise<void> {
  return new Promise((resolve) => {
    const onStateChange = () => {
      if (worker.state === "installing") return;
      worker.removeEventListener("statechange", onStateChange);
      resolve();
    };
    worker.addEventListener("statechange", onStateChange);
    // It may already have moved on between the read and this listener.
    onStateChange();
  });
}

/**
 * The worker that's ready to take over this page, or null if there isn't one.
 *
 * Two traps here, both of which leave a perfectly real update invisible in
 * `registration.waiting`:
 *
 * 1. `update()` resolves as soon as the new worker reaches "installing" — not
 *    when it's ready. Precaching the install takes real time (this app installs
 *    ~15 MB), so reading `waiting` straight after it is a coin flip. That's the
 *    window where recovery would otherwise reload under the OLD worker, get the
 *    same stale bundle, and burn the loop guard on the way past.
 * 2. Even once the install finishes, the `statechange` to "installed" is
 *    dispatched from a task queued BEFORE the one that updates the
 *    registration's `waiting` property, so `waiting` is still null when our
 *    listener runs. (This is why workbox-window waits 200ms and re-checks it.)
 *
 * Both are handled by holding on to the worker object we watched: a worker in
 * state "installed" IS the waiting worker, whatever the registration says yet.
 */
async function workerReadyToTakeOver(
  registration: Registration,
): Promise<PendingWorker | null> {
  // Usually the new worker is already waiting — `UpdatePrompt` re-checks on
  // every visibility change, and the toast is probably on screen. Only pay for
  // a `sw.js` fetch when nothing is pending, so the common path costs no
  // network round-trip (this runs with a half-rendered route on screen). A
  // failure here (offline, 5xx) just means there's nothing to activate.
  if (registration.waiting) return registration.waiting;
  if (!registration.installing) await registration.update().catch(() => {});

  const installing = registration.installing;
  if (!installing) return registration.waiting;

  // Wait it out rather than reloading now: a reload before the new worker is
  // ready is exactly the no-op this module exists to avoid, and it would spend
  // the one recovery attempt this chunk gets.
  await installSettled(installing);
  return installing.state === "installed" ? installing : registration.waiting;
}

/**
 * Reload onto the newest deployed build, activating a waiting service worker
 * first when there is one — without which the reload is just served the same
 * stale bundle out of the active worker's precache.
 */
export async function reloadIntoNewestBuild(
  deps: Pick<RecoveryDeps, "serviceWorker" | "reload">,
): Promise<void> {
  const { serviceWorker, reload } = deps;
  // No worker at all (unsupported, or never registered): the reload's
  // navigation goes to the network, which serves the newest `index.html`.
  if (!serviceWorker) return reload();

  const registration = await serviceWorker.getRegistration().catch(() => null);
  if (!registration) return reload();

  const ready = await workerReadyToTakeOver(registration);
  // Nothing newer to activate. Reload anyway: if this page isn't controlled,
  // that reaches the network and fixes it; if it is, the loop guard makes this
  // at most one wasted reload before the error surfaces.
  if (!ready) return reload();

  // `skipWaiting()` hands the new worker control of this client, so wait for
  // that to land — reloading before it does would be served the old precache.
  serviceWorker.addEventListener("controllerchange", () => reload(), {
    once: true,
  });
  ready.postMessage({ type: "SKIP_WAITING" });
}

/** Handle one `vite:preloadError`. Exported for tests; wired up below. */
export function handlePreloadError(
  event: PreloadErrorEvent,
  deps: RecoveryDeps,
): void {
  const err = event.payload;
  // The message is "Failed to fetch dynamically imported module: <url>" — the
  // hashed URL makes a stable per-chunk key.
  const key = err instanceof Error ? err.message : String(err);
  // Already tried this exact chunk: the reload didn't help, so let the error
  // surface instead of looping.
  if (deps.storage.getItem(RELOAD_KEY) === key) return;
  // Suppress Vite's default (rethrow) so the page navigates away cleanly
  // instead of flashing an unhandled rejection first. Must happen synchronously
  // — Vite reads `defaultPrevented` as soon as the dispatch returns.
  event.preventDefault();
  deps.storage.setItem(RELOAD_KEY, key);
  void reloadIntoNewestBuild(deps);
}

if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    handlePreloadError(event as PreloadErrorEvent, {
      serviceWorker:
        "serviceWorker" in navigator ? navigator.serviceWorker : undefined,
      storage: sessionStorage,
      reload: () => window.location.reload(),
    });
  });
}
